/**
 * @file localError.ts
 * @description Defines an enumeration for local (client-side) error codes with associated descriptions.
 */

/**
 * Enum representing local (client-side) error codes.
 * 
 * These error codes indicate various client-side issues, such as network failures
 * or server unavailability, that may prevent successful request execution.
 */
export enum LocalError {
  /**
   * Error code indicating a network connectivity issue.
   */
  NETWORK_ERROR = "N01",

  /**
   * Error code indicating that the server is currently down or unavailable.
   */
  SERVER_DOWN = "S01",

  /**
   * Error code indicating that the service is temporarily unavailable.
   */
  TEMPORARY_UNAVAILABLE = "T01",

  /**
   * Error code for unexpected or unclassified errors.
   */
  UNEXPECTED_ERROR = "U01",

  /**
   * Error code indicating a validation failure.
   */
  VALIDATION_ERROR = "V01"
}

export namespace LocalError {
  /**
   * Mapping of error codes to their corresponding error messages.
   */
  export const descriptions: Record<LocalError, string> = {
    [LocalError.NETWORK_ERROR]: "A network error occurred. Please check your internet connection and try again.",
    [LocalError.SERVER_DOWN]: "The server is currently unavailable. Please try again later.",
    [LocalError.TEMPORARY_UNAVAILABLE]: "The service is temporarily unavailable. Please try again after some time.",
    [LocalError.UNEXPECTED_ERROR]: "An unexpected error occurred. Please try again later.",
    [LocalError.VALIDATION_ERROR]: "Please fix the errors below",
  }

  /**
   * Retrieves the human-readable error message for a given error code.
   * @param errorCode The error code to look up
   * @returns The corresponding error message or a default fallback message
   */
  export function getDescription(errorCode: LocalError): string {
    return descriptions[errorCode] || "An unknown error occurred."
  }
}